import InventoryStore from 'admin/InventoryStore'
import { AlertFactory } from 'alerts/Alert'
import { IWorkspaceSearcher } from 'components/WorkspaceSelectorViewHeading'
import Workspace from 'iam/models/Workspace'
import { action, computed, observable, makeObservable } from 'mobx'
import { of, Subject, Subscription } from 'rxjs'
import { delay, map, startWith, switchMap } from 'rxjs/operators'
import { fromStream } from 'utils/mobx-util'
import { shareLatest } from 'utils/rx-util'

export default class WorkspaceSearchViewModel implements IWorkspaceSearcher {
  /**
   * User is searching a workspace
   */
  @observable editing = false
  /**
   * Observable subscription to searches$ used for disposing on deactivate.
   */
  private searchSubscription = new Subscription()

  /**
   * Search text changes.
   */
  private searchTextChange = new Subject<string>()

  /**
   * Searches observable. Each emitted value is the search result of
   * a completed query.
   */
  searches$ = // When the search filter changes
    this.searchTextChange.pipe(
      switchMap((change) =>
        // Apply the delay if debouncing
        of(change).pipe(delay(300)),
      ),
      startWith(''),
      // Make sure to only subscribe once.
      shareLatest(),
    )

  /**
   * The search text as a computed, derived from the search text change stream.
   */
  private getSearchText = fromStream(
    '',
    this.searchTextChange.pipe(map((value) => value)),
  )

  constructor(
    private newAlert: AlertFactory,
    private inventoryStore: InventoryStore,
  ) {
    makeObservable(this)
  }

  @action.bound
  startEditing() {
    this.editing = true
  }

  @action.bound
  stopEditing() {
    this.editing = false
  }

  private async _fetchWorkspacesWithInventoryItems() {
    try {
      await this.inventoryStore.fetchWorkspacesWithInventoryItems()
    } catch (error) {
      this.newAlert().fromError(error).show()
      console.error(error)
    }
  }

  /**
   * Search text computed getter.
   */
  @computed
  get searchText() {
    return this.getSearchText()
  }

  /**
   * Returns only active workspaces
   */
  @computed
  get activeWorkspaces() {
    return this.inventoryStore.workspacesWithInventoryItems.filter(
      (item) => item.workspace !== Workspace.none,
    )
  }

  /**
   * Filtered workspaces
   */
  @computed
  get filteredWorkspaces() {
    const result = this.activeWorkspaces

    if (this.searchText) {
      const searchText = this.searchText.toLowerCase()

      return result.filter((item) =>
        item.workspace?.name.toLocaleLowerCase().includes(searchText),
      )
    }
    return result
  }

  /**
   * Sets the search text.
   *
   * @param searchText
   */
  @action.bound
  setSearchText(searchText: string) {
    this.searchTextChange.next(searchText)
  }

  async activate() {
    await this._fetchWorkspacesWithInventoryItems()
    this.searchSubscription = this.searches$.subscribe()
  }

  /**
   * Called when the view model is not being actively used.
   */
  deactivate() {
    this.searchSubscription.unsubscribe()
  }
}
